import { useEffect, useState } from "react";
import {
  fetchQuery,
  postsByCategoryQuery,
  categoryBySlugQuery,
  type Post,
  type Category,
} from "@/lib/sanity";
import { ArticleList } from "@/components/ui/article/articleList";
import { ContactCard } from "@/components/ui/contact/contactCard";

type ArticlesByCategoryPageProps = {
  categorySlug: string;
  onNavigate?: (path: string) => void;
};

export function ArticlesByCategoryPage({
  categorySlug,
  onNavigate,
}: ArticlesByCategoryPageProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchQuery<Post[]>(postsByCategoryQuery, { categorySlug }),
      fetchQuery<Category | null>(categoryBySlugQuery, { slug: categorySlug }),
    ])
      .then(([postsData, categoryData]) => {
        if (!cancelled) {
          setPosts(Array.isArray(postsData) ? postsData : []);
          setCategory(categoryData ?? null);
        }
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load articles");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  const displayName = category?.title ?? categorySlug;

  return (
    <>
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-3xl">
          <nav className="mb-8" aria-label="Breadcrumb">
            <button
              type="button"
              onClick={() => go("/articles")}
              className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              記事一覧
            </button>
            <span className="mx-1.5 text-muted-foreground/60">/</span>
            <span className="text-sm text-foreground" aria-current="page">
              {displayName}
            </span>
          </nav>
          <h1 className="font-serif text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
            {displayName}
          </h1>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
            「{displayName}」カテゴリの記事一覧です。
          </p>
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-6xl">
          {loading && (
            <p className="py-16 text-center text-muted-foreground">読み込み中…</p>
          )}
          {error && (
            <p className="py-16 text-center text-destructive">{error}</p>
          )}
          {!loading && !error && (
            <ArticleList posts={posts} onNavigate={go} />
          )}
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <ContactCard />
        </div>
      </section>
    </>
  );
}
